import * as THREE from 'three';

import {
  BOOTH,
  DEFAULT_WALL_HEIGHT,
  NEIGHBOR_GRAPHICS_RULE,
  SCREEN,
  type WallHeight,
} from '../config/booth';
import { BRAND } from '../textures/canvas';
import {
  makeHeadline,
  makeLeftPanel,
  makeRightPanel,
} from '../textures/panels';

const WALL_T = 0.35;
/** Navy header band across the top of every wall, as in the rendering. */
const HEADER = 1.0;
/** Thickness of the red LED edge strips. */
const LED = 0.08;

const mats = {
  white: new THREE.MeshStandardMaterial({
    color: BRAND.offWhite,
    roughness: 0.55,
  }),
  navy: new THREE.MeshStandardMaterial({ color: BRAND.navy, roughness: 0.6 }),
  neutral: new THREE.MeshStandardMaterial({
    color: '#d7d7d2',
    roughness: 0.85,
  }),
  led: new THREE.MeshStandardMaterial({
    color: BRAND.red,
    emissive: BRAND.red,
    emissiveIntensity: 2.2,
    roughness: 0.3,
  }),
  ledSoft: new THREE.MeshStandardMaterial({
    color: BRAND.red,
    emissive: BRAND.red,
    emissiveIntensity: 1.2,
    roughness: 0.3,
  }),
};

function box(w: number, h: number, d: number, mat: THREE.Material): THREE.Mesh {
  const m = new THREE.Mesh(new THREE.BoxGeometry(w, h, d), mat);
  m.castShadow = true;
  m.receiveShadow = true;
  return m;
}

function panel(
  tex: THREE.Texture,
  w: number,
  h: number,
  emissive = 0,
  transparent = false,
): THREE.Mesh {
  const mat = new THREE.MeshStandardMaterial({
    map: tex,
    transparent,
    roughness: 0.5,
    ...(emissive
      ? { emissive: '#ffffff', emissiveMap: tex, emissiveIntensity: emissive }
      : {}),
  });
  const m = new THREE.Mesh(new THREE.PlaneGeometry(w, h), mat);
  m.receiveShadow = true;
  return m;
}

export interface Booth {
  group: THREE.Group;
  setHeight(h: WallHeight): void;
  setRuleZoneVisible(on: boolean): void;
}

/**
 * Walls, header band, LED edges and the graphics. Everything that depends on
 * the wall height lives in `rebuild`, so the 8'/10' switch just swaps it.
 */
export function buildBooth(logo: THREE.Texture): Booth {
  const group = new THREE.Group();
  const walls = new THREE.Group();
  group.add(walls);
  const W = BOOTH.width;
  const D = BOOTH.depth;

  // Graphics that do not move with the height.
  const headline = panel(makeHeadline(), 4.6, 0.79, 0, true);
  headline.position.set(
    SCREEN.x + 2.55,
    SCREEN.bottom + SCREEN.height + 0.5,
    WALL_T + 0.01,
  );
  group.add(headline);

  const logoMat = new THREE.MeshStandardMaterial({
    map: logo,
    transparent: true,
    roughness: 0.5,
  });
  const logoPlane = new THREE.Mesh(
    new THREE.PlaneGeometry(4.0, 4.0 * (437 / 3000)),
    logoMat,
  );
  logoPlane.position.set(
    SCREEN.x - 2.65,
    SCREEN.bottom + SCREEN.height + 0.5,
    WALL_T + 0.01,
  );
  group.add(logoPlane);

  const left = panel(makeLeftPanel(), 4.2, 8.4 * 0.95);
  left.position.set(W - 2.6, 4.1, WALL_T + 0.01);
  group.add(left);

  const right = panel(makeRightPanel(), 3.0, 7.5);
  right.rotation.y = Math.PI / 2;
  right.position.set(WALL_T + 0.01, 3.9, D - 2.2);
  group.add(right);

  // Neighbour-rule zone: graphics above 8' must be 10' from the neighbour.
  const zone = new THREE.Mesh(
    new THREE.PlaneGeometry(NEIGHBOR_GRAPHICS_RULE.setback, D),
    new THREE.MeshBasicMaterial({
      color: BRAND.red,
      transparent: true,
      opacity: 0.18,
      depthWrite: false,
    }),
  );
  zone.rotation.x = -Math.PI / 2;
  zone.position.set(NEIGHBOR_GRAPHICS_RULE.setback / 2, 0.02, D / 2);
  zone.visible = false;
  group.add(zone);

  function rebuild(h: WallHeight): void {
    walls.clear();
    // Back wall: white face, navy header, neutral finished back (it faces the row behind).
    const back = box(W, h, WALL_T, mats.white);
    back.position.set(W / 2, h / 2, WALL_T / 2);
    walls.add(back);
    const backHeader = box(W, HEADER, WALL_T + 0.04, mats.navy);
    backHeader.position.set(W / 2, h - HEADER / 2, WALL_T / 2);
    walls.add(backHeader);
    const backKick = box(W, 0.5, WALL_T + 0.04, mats.navy);
    backKick.position.set(W / 2, 0.25, WALL_T / 2);
    walls.add(backKick);

    // Return wall on the neighbour (left) side: navy toward us, neutral toward 44349 (rule §9).
    const ret = box(WALL_T, h, D, mats.navy);
    ret.position.set(WALL_T / 2, h / 2, D / 2);
    walls.add(ret);
    const retBack = box(0.05, h, D, mats.neutral);
    retBack.position.set(-0.02, h / 2, D / 2);
    walls.add(retBack);

    // Red LED edges: along the top of both walls and down the leading edges.
    const topBack = box(W, LED, LED, mats.led);
    topBack.position.set(W / 2, h + LED / 2, WALL_T + LED / 2);
    walls.add(topBack);
    const topRet = box(LED, LED, D, mats.led);
    topRet.position.set(WALL_T + LED / 2, h + LED / 2, D / 2);
    walls.add(topRet);
    const edgeOpen = box(LED, h, LED, mats.led);
    edgeOpen.position.set(W - LED / 2, h / 2, WALL_T + LED / 2);
    walls.add(edgeOpen);
    const edgeFront = box(LED, h, LED, mats.led);
    edgeFront.position.set(WALL_T + LED / 2, h / 2, D - LED / 2);
    walls.add(edgeFront);
    const floorGlow = box(W, LED, LED, mats.ledSoft);
    floorGlow.position.set(W / 2, 0.5 + LED / 2, WALL_T + LED / 2);
    walls.add(floorGlow);
    const floorGlowRet = box(LED, LED, D, mats.ledSoft);
    floorGlowRet.position.set(WALL_T + LED / 2, 0.5 + LED / 2, D / 2);
    walls.add(floorGlowRet);
  }

  rebuild(DEFAULT_WALL_HEIGHT);
  return {
    group,
    setHeight: rebuild,
    setRuleZoneVisible: (on) => {
      zone.visible = on;
    },
  };
}
